import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setUser } from '../../store/slices/userSlice';
import authService, { AuthResponse, ValidateUserResponse } from '../../services/authService';
import {
  getLoginAuthSettings,
  formatUsernameInput,
  normalizeUsernameForSubmit,
  validateUsername,
  LOGIN_FORM_AUTOCOMPLETE,
} from '../../config/authConfig';
import { navigateAfterAuth } from '../../utils/postLoginNavigation';
import { trackEvent } from '../../utils/analytics';
import {
  LOGIN_GO_KEYBOARD_PROPS,
  useLoginFieldFocus,
  handleEnterOrGoKeyDown,
} from '../../utils/loginKeyboard';
import { consumeSocialAuthError } from '../../utils/socialAuth';
import AuthMessage from '../AuthMessage/AuthMessage';
import PasswordField from '../common/PasswordField/PasswordField';
import SocialLoginButtons from '../SocialLoginButtons/SocialLoginButtons';
import AuthPageLayout from '../AuthPageLayout/AuthPageLayout';
import './SignIn.css';

const SignIn: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user, isAuthenticated, authReady } = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<number>(1);
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [otp, setOtp] = useState<string[]>(['', '', '', '']);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isNewUser, setIsNewUser] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isPartners, setIsPartners] = useState<boolean>(false);
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const [urlBanner, setUrlBanner] = useState<{ type: 'error' | 'success'; message: string } | null>(null);
  const redirectAttemptedRef = useRef<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null);
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const otpFirstRef = useRef<HTMLInputElement>(null);

  // Check if current subdomain is "partners"
  useEffect(() => {
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0]?.toLowerCase();
    setIsPartners(subdomain === 'partners');
  }, []);

  // Timer countdown effect
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  useLoginFieldFocus(usernameRef, step === 1 && !loading);
  useLoginFieldFocus(passwordRef, step === 2 && !isMobile && !loading);
  useLoginFieldFocus(otpFirstRef, step === 2 && isMobile && !loading);

  useEffect(() => {
    if (searchParams.get('password_changed')) {
      setUrlBanner({
        type: 'success',
        message: 'Your password has been changed. Sign in with your new password.',
      });
    } else if (searchParams.get('social_error') || searchParams.get('error') === 'social') {
      const socialError = consumeSocialAuthError();
      setUrlBanner({
        type: 'error',
        message: socialError || 'Google or Facebook sign-in failed. Try again, or continue with email / OTP.',
      });
    } else if (searchParams.get('error')) {
      setUrlBanner({
        type: 'error',
        message: 'Incorrect email or password. Please try again.',
      });
    }
  }, [searchParams]);

  const continueSignedIn = useCallback(async () => {
    const me = await authService.getCurrentUser();
    const redirectUrl = me.redirectUrl || '/';
    await navigateAfterAuth(redirectUrl, {
      urlRedirect: searchParams.get('redirect'),
      isPartners,
      navigate,
      roleId: me.user?.roleId ?? user?.roleId,
    });
  }, [isPartners, navigate, searchParams, user?.roleId]);

  // Already signed in — leave /signin. The merchant WebView restores this URL
  // after a deploy, and staying here makes the next login fail.
  useEffect(() => {
    if (!authReady || !isAuthenticated || !user || redirectAttemptedRef.current) {
      return;
    }

    redirectAttemptedRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        if (cancelled) {
          return;
        }
        await continueSignedIn();
      } catch (err) {
        console.error('Error redirecting signed-in user:', err);
        redirectAttemptedRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authReady, isAuthenticated, user, continueSignedIn]);

  const loginAuth = getLoginAuthSettings(isPartners);

  const handleValidateUser = async () => {
    const validationError = validateUsername(username, loginAuth);
    if (validationError) {
      setError(validationError);
      return;
    }

    const normalizedUsername = normalizeUsernameForSubmit(username, loginAuth);
    if (normalizedUsername !== username) {
      setUsername(normalizedUsername);
    }

    setLoading(true);
    setError('');

    try {
      const response: ValidateUserResponse = await authService.validateUser(normalizedUsername);
      
      if (response.success) {
        const mobileFlow = !loginAuth.emailOnly && (response.isMobile || false);
        setIsMobile(mobileFlow);
        setIsNewUser(response.newUser || false);
        // Update isPartners from API response if provided
        if (response.isPartners !== undefined) {
          setIsPartners(response.isPartners);
        }
        setStep(2);
        // Start cooldown timer if mobile (use value from API or default to 30)
        if (mobileFlow) {
          const cooldownSeconds = response.otpCooldownSeconds || 30;
          setResendCooldown(cooldownSeconds);
          setSuccessMessage(''); // Clear success message for mobile
        } else if (response.newUser) {
          // Show success message for new email users (use message from API if available)
          setSuccessMessage(response.message || 'A password has been sent to your email address. Please check your email and use that password to login.');
          setError(''); // Clear any errors
        } else {
          setSuccessMessage(''); // Clear success message for existing users
        }
      } else {
        setError(response.error || 'User validation failed');
        setSuccessMessage(''); // Clear success message on error
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred');
      setSuccessMessage(''); // Clear success message on error
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) {
      return;
    }
    if (step === 1) {
      await handleValidateUser();
    } else {
      await handleLogin(e);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const normalizedUsername = normalizeUsernameForSubmit(username, loginAuth);

    try {
      const otpValue = isMobile ? otp.join('') : password;
      const response = await authService.login(normalizedUsername, otpValue);
      
      // After successful login, fetch user info and store in Redux
      if (response.success) {
        trackEvent('sign_in', { method: isMobile ? 'otp' : 'password' });
        let fetchedUser: { roleId?: number } | undefined;
        try {
          const userResponse = await authService.getCurrentUser();
          if (userResponse.success && userResponse.user) {
            fetchedUser = userResponse.user;
            dispatch(setUser(userResponse.user));
          }
        } catch (err) {
          console.error('Error fetching user after login:', err);
        }

        const urlRedirect = searchParams.get('redirect');
        const redirectUrl = urlRedirect || response.redirectUrl || '/';
        await navigateAfterAuth(redirectUrl, {
          urlRedirect,
          isPartners,
          navigate,
          roleId: fetchedUser?.roleId,
        });
      }
    } catch (err: any) {
      try {
        const me = await authService.getCurrentUser();
        if (me.success && me.user) {
          dispatch(setUser(me.user));
          await navigateAfterAuth(me.redirectUrl || '/', {
            urlRedirect: searchParams.get('redirect'),
            isPartners,
            navigate,
            roleId: me.user.roleId,
          });
          return;
        }
      } catch {
        // Fall through to the login error.
      }
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSuccess = async (response: AuthResponse) => {
    let roleId = response.user?.roleId;
    if (response.user) {
      dispatch(setUser(response.user));
    } else {
      try {
        const userResponse = await authService.getCurrentUser();
        if (userResponse.success && userResponse.user) {
          roleId = userResponse.user.roleId;
          dispatch(setUser(userResponse.user));
        }
      } catch (err) {
        console.error('Error fetching user after social login:', err);
      }
    }

    const urlRedirect = searchParams.get('redirect');
    const redirectUrl = urlRedirect || response.redirectUrl || '/';
    await navigateAfterAuth(redirectUrl, {
      urlRedirect,
      isPartners,
      navigate,
      roleId,
    });
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 3) {
      const nextInput = document.getElementById(`digit-${index + 2}`);
      nextInput?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`digit-${index}`);
      prevInput?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    const digits = pastedData.replace(/[^0-9]/g, '').slice(0, 4).split('');
    
    if (digits.length > 0) {
      const newOtp = [...otp];
      digits.forEach((digit, idx) => {
        if (idx < 4) {
          newOtp[idx] = digit;
        }
      });
      setOtp(newOtp);
      
      // Focus on the last filled input or the last input if all are filled
      const lastFilledIndex = Math.min(digits.length - 1, 3);
      const nextInput = document.getElementById(`digit-${lastFilledIndex + 1}`);
      nextInput?.focus();
    }
  };

  return (
    <AuthPageLayout>
      <div className="signin-container">
        <div className="signin-wrapper">
          <div className="signin-form">
            <h2 className="signin-title text-center">
              {isPartners ? 'Partner Login/Register' : 'Login/Register with Peppar'}
            </h2>
            <div className="register-text">
              {isPartners ? (
                <>
                  Restaurant partner? <span className="register-link">Get started here</span>
                </>
              ) : (
                <>
                  New to Peppar? <span className="register-link">Continue here</span>
                </>
              )}
            </div>

            {urlBanner && (
              <AuthMessage variant={urlBanner.type}>{urlBanner.message}</AuthMessage>
            )}

            {authReady && isAuthenticated && user && (
              <div className="auth-message-block">
                <AuthMessage variant="success">You are already signed in.</AuthMessage>
                <button
                  type="button"
                  className="btn-style-2"
                  disabled={loading}
                  onClick={() => {
                    setLoading(true);
                    setError('');
                    continueSignedIn()
                      .catch(() => {
                        setError('Could not open your dashboard. Try again.');
                      })
                      .finally(() => setLoading(false));
                  }}
                >
                  Continue
                </button>
              </div>
            )}

            <form ref={formRef} onSubmit={handleFormSubmit} className="signin-form-body" autoComplete="on">
              {step === 1 ? (
                <div id="step-1">
                  <div className="auth-message-block">
                    {error && <AuthMessage variant="error">{error}</AuthMessage>}
                    {successMessage && <AuthMessage variant="success">{successMessage}</AuthMessage>}
                  </div>
                  <SocialLoginButtons
                    onSuccess={handleSocialSuccess}
                    onError={setError}
                    disabled={loading}
                  />
                  <h5>{loginAuth.usernameLabel}</h5>
                  <input
                    ref={usernameRef}
                    type={loginAuth.emailOnly ? 'email' : 'text'}
                    name={step === 1 ? 'username' : undefined}
                    id="username"
                    className="input-res"
                    placeholder={loginAuth.usernamePlaceholder}
                    autoComplete={LOGIN_FORM_AUTOCOMPLETE.username}
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    inputMode={loginAuth.emailOnly ? 'email' : 'text'}
                    autoFocus
                    {...LOGIN_GO_KEYBOARD_PROPS}
                    value={username}
                    onChange={(e) => setUsername(formatUsernameInput(e.target.value, loginAuth))}
                    onKeyDown={(e) => handleEnterOrGoKeyDown(e, formRef)}
                    disabled={loading}
                  />
                  <button
                    type="submit"
                    className="btn-style-2"
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Continue'}
                  </button>
                </div>
              ) : (
                <div id="step-2">
                  <div className="go-back" onClick={() => { setStep(1); setError(''); setSuccessMessage(''); }}>
                    <i className="fa fa-long-arrow-left" aria-hidden="true"></i> Back
                  </div>
                  <div className="auth-message-block">
                    {error && <AuthMessage variant="error">{error}</AuthMessage>}
                    {successMessage && <AuthMessage variant="success">{successMessage}</AuthMessage>}
                  </div>

                  {isMobile ? (
                    <div className="otp-input-digits">
                      <h5>Please enter the OTP sent to your phone</h5>
                      <div className="digit-group">
                        {otp.map((digit, index) => (
                          <input
                            key={index}
                            ref={index === 0 ? otpFirstRef : undefined}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            id={`digit-${index + 1}`}
                            className="form-control otp-input"
                            maxLength={1}
                            value={digit}
                            {...(index === 3 ? LOGIN_GO_KEYBOARD_PROPS : { enterKeyHint: 'next' as const })}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^0-9]/g, '');
                              handleOtpChange(index, value);
                            }}
                            onKeyDown={(e) => {
                              handleOtpKeyDown(index, e);
                              if (index === 3) {
                                handleEnterOrGoKeyDown(e, formRef);
                              }
                            }}
                            onPaste={handleOtpPaste}
                          />
                        ))}
                      </div>
                      <h6 style={{ textAlign: 'center', marginTop: '15px', color: '#666', fontSize: '14px' }}>
                        Didn't get an OTP?{' '}
                        {resendCooldown > 0 ? (
                          <span style={{ color: '#999' }}>
                            Resend OTP in {resendCooldown}s
                          </span>
                        ) : (
                          <a 
                            className="need-help" 
                            href="#" 
                            onClick={(e) => { 
                              e.preventDefault(); 
                              if (!loading) {
                                handleValidateUser(); 
                              }
                            }}
                          >
                          Re-send OTP
                        </a>
                        )}
                      </h6>
                    </div>
                  ) : (
                    <div className="password-login">
                      <h5 className="password-input-title">
                        {isNewUser 
                          ? "Please enter your password sent over an email to login"
                          : "Please enter your password to login"}
                      </h5>
                      <input
                        type="text"
                        name="username"
                        value={normalizeUsernameForSubmit(username, loginAuth)}
                        autoComplete={LOGIN_FORM_AUTOCOMPLETE.username}
                        readOnly
                        tabIndex={-1}
                        aria-hidden="true"
                        className="login-autofill-bridge"
                      />
                      <PasswordField
                        ref={passwordRef}
                        id="password"
                        name="password"
                        inputClassName="input-res"
                        required
                        placeholder="Enter your password"
                        autoComplete={LOGIN_FORM_AUTOCOMPLETE.currentPassword}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        {...LOGIN_GO_KEYBOARD_PROPS}
                        onKeyDown={(e) => handleEnterOrGoKeyDown(e, formRef)}
                        disabled={loading}
                      />
                      <div className="forgot-password">
                        <Link to="/forgotPassword" className="forgot-password">
                          Forgot your password?
                        </Link>
                      </div>
                    </div>
                  )}

                  <button type="submit" className="btn-style-2" disabled={loading}>
                    {loading ? 'Logging in...' : 'LOGIN'}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </AuthPageLayout>
  );
};

export default SignIn;

